import { useEffect, useState } from "react";
import { EonUiButton, EonUiIcon } from "@eon-ui/eon-ui-components-react";
import { Icon } from "@me8eon/icons";
import { useUserData } from "@me8eon/react_authentication";
import type { DecisionRecord, ItemAction, ItemDetail } from "../aidwops.types";
import { ApiError } from "../expertApi";
import useAidwopsStore from "../AidwopsStore";
import { BatteryDevicesSection } from "./BatteryDevicesSection";
import { CommentBox } from "./CommentBox";
import { LifecycleStepper } from "./LifecycleStepper";
import { PolicyHygieneSection } from "./PolicyHygieneSection";
import "./ExpertQueue.scss";
import { ProcessingStepper } from "./ProcessingStepper";
import LinkedTicketsView from "./LinkedTickets";

type ProcessingOutcome = "pending" | "success" | "failed";

interface QueueItemDetailCardProps {
    item: ItemDetail;
    /** Whose side of the request this panel shows. The ISO sees the same facts and decides instead of the engineer. */
    perspective?: "engineer" | "iso";
    /** For the ISO's side: whether the signed-in person is a listed approver (`summary.canDecide`). Everyone else can only read. */
    canIsoDecide?: boolean;
    /** Does what the person confirmed and answers with the item as the backend now has it. */
    onAction: (action: ItemAction, reason: string) => Promise<ItemDetail>;
    /** Adds a note to the request's Jira issue. Leave out to hide the note box. */
    onComment?: (text: string) => Promise<ItemDetail>;
    /** Re-reads the item (and the queue). Used when the item changed since it was opened (409). */
    onReload?: () => void | Promise<void>;
    /** Closes the panel. Defaults to deselecting the item in the chat. */
    onDismiss?: () => void;
}

/** What went wrong when an action could not be saved, so the failure screen can say why and offer the right next step. */
interface ActionFailure {
    message: string;
    /** The item changed since it was opened: the same call can never succeed until it is reloaded. */
    stale: boolean;
    /** The pre-flight checks that block the request, when the backend names them. */
    reasons: string[];
}

/** The words each action uses on the submit, success and failure screens. */
interface ActionCopy {
    /** The heading of the submit step and the button that confirms it. */
    verb: string;
    /** Past tense, for the success title. */
    done: string;
    /** What failed, for the failure title ("Approval failed"). */
    noun: string;
    /** The word on the summary line. */
    note: string;
    /** What the text box is called, and what its content is called once saved. */
    field: string;
    word: string;
    stateHint: string;
    confirm: string;
    submitSubtitle: string;
    failSubtitle: string;
    placeholder: string;
}

const ACTION_COPY: Record<ItemAction, ActionCopy> = {
    approve: {
        verb: "Approve",
        done: "Approved",
        noun: "Approval",
        note: "APPROVED",
        field: "Your comment",
        word: "Comment",
        stateHint: 'State set to "Approved"',
        confirm: "I checked the scope, target, and proposed action.",
        submitSubtitle: "Review your comment, then confirm.",
        failSubtitle: "Your decision could not be saved.",
        placeholder: "",
    },
    decline: {
        verb: "Decline",
        done: "Declined",
        noun: "Decline",
        note: "DECLINED",
        field: "Your comment",
        word: "Comment",
        stateHint: 'State set to "Declined"',
        confirm: "I checked the scope, target, and proposed action.",
        submitSubtitle: "Review your comment, then confirm.",
        failSubtitle: "Your decision could not be saved.",
        placeholder: "",
    },
    rollback: {
        verb: "Roll back",
        done: "Rolled back",
        noun: "Rollback",
        note: "ROLLBACK",
        field: "Your reason",
        word: "Reason",
        stateHint: 'State set to "Rolled back"',
        confirm: "I confirmed the item and rollback action.",
        submitSubtitle: "Review your reason, then confirm the rollback.",
        failSubtitle: "The rollback could not be completed.",
        placeholder: "",
    },
    close: {
        verb: "Close request",
        done: "Closed",
        noun: "Closing",
        note: "CLOSED",
        field: "Closing remark",
        word: "Remark",
        stateHint: 'State set to "Completed"',
        confirm: "I checked what was applied and the final scope.",
        submitSubtitle:
            "Say what was applied, then close the request and its Jira issue.",
        failSubtitle: "The request could not be closed.",
        placeholder: "What was applied and the final scope",
    },
    retry: {
        verb: "Retry apply",
        done: "Retried",
        noun: "Retry",
        note: "RETRY",
        field: "",
        word: "",
        stateHint: "",
        confirm: "",
        submitSubtitle: "",
        failSubtitle: "The write to Zscaler failed again.",
        placeholder: "",
    },
    iso_approve: {
        verb: "Approve",
        done: "Approved",
        noun: "Approval",
        note: "ISO APPROVED",
        field: "Your note (optional)",
        word: "Note",
        stateHint: 'State set to "Approved"',
        confirm: "I checked the scope and the pre-flight.",
        submitSubtitle: "Add a note if you like, then confirm.",
        failSubtitle: "Your decision could not be saved.",
        placeholder: "",
    },
    iso_reject: {
        verb: "Reject",
        done: "Rejected",
        noun: "Rejection",
        note: "ISO REJECTED",
        field: "Your reason",
        word: "Reason",
        stateHint: 'State set to "Rejected"',
        confirm: "I checked the scope and the pre-flight.",
        submitSubtitle: "Say why: the requester sees it.",
        failSubtitle: "Your decision could not be saved.",
        placeholder: "Why this request is rejected",
    },
};

/** How many characters the backend wants for an action's text. An ISO approval may go without a note; a retry has no text. */
function minLengthFor(action: ItemAction, item: ItemDetail): number {
    const min = item.requires.reasonMinLength;
    switch (action) {
        case "approve":
        case "decline":
            return min;
        case "rollback":
        case "close":
        case "iso_reject":
            return Math.max(min, 12);
        default:
            return 0;
    }
}

function defaultTextFor(action: ItemAction, item: ItemDetail): string {
    switch (action) {
        case "approve":
            return `Evidence reviewed and consistent with the request. Priority ${item.priority.score}/100, ${item.risk} risk.`;
        case "decline":
            return "Policy prohibits exceptions and the request has no supported business reason.";
        case "rollback":
            return "Rollback requested after review.";
        default:
            return "";
    }
}

function formatRelativeTime(isoString?: string | null): string {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;

    const diffSeconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diffSeconds < 0 || diffSeconds < 60) {
        return "just now";
    }
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) {
        return `${diffMinutes} min ago`;
    }
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
        return `${diffHours} ${diffHours === 1 ? "hr" : "hrs"} ago`;
    }
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) {
        return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
    }
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) {
        return `${diffMonths} ${diffMonths === 1 ? "month" : "months"} ago`;
    }
    const diffYears = Math.floor(diffDays / 365);
    return `${diffYears} ${diffYears === 1 ? "yr" : "yrs"} ago`;
}

function hoursAgo(hours: number): string {
    if (hours < 1) return "less than an hour ago";
    if (hours < 48) {
        const whole = Math.round(hours);
        return `${whole} ${whole === 1 ? "hour" : "hours"} ago`;
    }
    return `${Math.round(hours / 24)} days ago`;
}

const PRODUCTION_VERDICT: Record<
    NonNullable<ItemDetail["production"]>["verdict"],
    { text: string; tone: string }
> = {
    blocked: { text: "Production blocks it today", tone: "context" },
    allowed: { text: "Production already allows it", tone: "high" },
    caution: { text: "Production cautions it", tone: "medium" },
    unknown: { text: "Production state unknown", tone: "kind" },
};

function Worknote({
    workNoteLines,
    title = "WORK NOTE PREVIEW",
}: {
    workNoteLines: string[];
    title?: string;
}) {
    return (
        <div className="expert-queue-item-detail-card-work-note-box">
            <div className="expert-queue-item-detail-card-work-note-head">
                <span>▤ {title}</span>
            </div>
            <pre className="expert-queue-item-detail-card-note">
                {workNoteLines.join("\n")}
            </pre>
        </div>
    );
}

export function CheckIcon({ status }: { status: "pass" | "warn" | "fail" }) {
    return (
        <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            {status === "pass" ? (
                <polyline points="20 6 9 17 4 12" />
            ) : status === "warn" ? (
                <>
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </>
            ) : (
                <>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                </>
            )}
        </svg>
    );
}

function DecisionLine({
    label,
    record,
    outcome,
}: {
    label: string;
    record: DecisionRecord;
    outcome?: string;
}) {
    return (
        <div className="expert-queue-item-detail-card-kv-row">
            <span className="expert-queue-item-detail-card-kv-label">
                {label}
            </span>
            <span className="expert-queue-item-detail-card-kv-value">
                {outcome ? `${outcome} by ` : ""}
                {record.by}
                {record.at ? ` · ${formatRelativeTime(record.at)}` : ""}
                {record.comment ? ` — ${record.comment}` : ""}
            </span>
        </div>
    );
}

export function QueueItemDetailCard({
    item,
    perspective = "engineer",
    canIsoDecide = false,
    onAction,
    onComment,
    onReload,
    onDismiss,
}: QueueItemDetailCardProps) {
    const { name: loggedInName } = useUserData();
    const setSelectedItem = useAidwopsStore((state) => state.setSelectedItem);
    const pendingDraft = useAidwopsStore((state) => state.pendingDraft);
    const clearPendingDraft = useAidwopsStore(
        (state) => state.clearPendingDraft
    );
    const reviewerName = loggedInName || "you";
    const dismiss = onDismiss ?? (() => setSelectedItem(null));
    const isIso = perspective === "iso";

    const [stage, setStage] = useState<
        "preview" | "submit" | "success" | "failure"
    >("preview");
    const [pendingAction, setPendingAction] = useState<ItemAction>("approve");
    const [reason, setReason] = useState("");
    const [confirmed, setConfirmed] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [actionResult, setActionResult] = useState<ItemDetail | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [actionFailure, setActionFailure] = useState<ActionFailure | null>(
        null
    );

    useEffect(() => {
        setStage("preview");
        setReason("");
        setConfirmed(false);
        setActionError(null);
        setActionFailure(null);
        setActionResult(null);

        setProcessingAction(null);
        setProcessingOutcome("pending");
        setPendingActionResult(null);
        setPendingActionFailure(null);
        setIsSubmitting(false);
    }, [item.id]);

    const copy = ACTION_COPY[pendingAction];
    const requiredLength = minLengthFor(pendingAction, item);

    // The recommended primary action: the backend can recommend declining (e.g. production already allows the
    // URL, so approving would only be refused), not just approving. The button must submit whichever one it
    // actually names, or a "Decline the request" button would file an approval and fail every time.
    const recommendedAction: "approve" | "decline" =
        item.recommendation.action === "decline" ? "decline" : "approve";

    // These kinds have their own per-device/per-policy triage panel (BatteryDevicesSection/PolicyHygieneSection)
    // with a "Stage SNOW ..." button that already raises the ServiceNow request directly, so the generic
    // item-level Approve/Decline footer would just be a redundant, confusing second way to act on the same item.
    const hasTriagePanel =
        item.kind === "intune_battery_attention" ||
        item.kind === "intune_policy_hygiene";

    // What the person can do depends on whose side this is and where the request is. The backend refuses anything else with a 409.
    const canEngineerDecide =
        !isIso && !hasTriagePanel && item.status === "awaiting_review";
    // An `apply_failed` item needs a retry (a different route) and an `applied` one needs a closing remark, not a new decision.
    const canRetry = !isIso && item.status === "apply_failed";
    const canCloseRequest = !isIso && item.status === "applied";
    // Rollback is an engineer-only post-closure action. `approved_staged` and `applied` changes cannot be undone here.
    const canRollback = !isIso && item.status === "closed";
    const canIsoAct = isIso && canIsoDecide && item.status === "waiting_iso";

    const [processingAction, setProcessingAction] = useState<
        "retry" | "rollback" | null
    >(null);

    const [processingOutcome, setProcessingOutcome] =
        useState<ProcessingOutcome>("pending");

    const [pendingActionResult, setPendingActionResult] =
        useState<ItemDetail | null>(null);

    const [pendingActionFailure, setPendingActionFailure] =
        useState<ActionFailure | null>(null);

    const [processingRunId, setProcessingRunId] = useState(0);

    const processingSteps =
        processingAction === "rollback"
            ? [
                  {
                      key: "record-rollback",
                      label: "Record Rollback Decision",
                  },
                  {
                      key: "rollback-change",
                      label: "Rollback Zscaler Change",
                  },
                  {
                      key: "verify-result",
                      label: "Verify Result",
                  },
                  {
                      key: "post-note",
                      label: "Post Work Note to ServiceNow",
                  },
              ]
            : [
                  {
                      key: "record-retry",
                      label: "Record Retry",
                  },
                  {
                      key: "retry-change",
                      label: "Retry Zscaler Change",
                  },
                  {
                      key: "verify-result",
                      label: "Verify Result",
                  },
                  {
                      key: "post-note",
                      label: "Post Work Note to ServiceNow",
                  },
              ];

    const canDo = (action: ItemAction): boolean => {
        switch (action) {
            case "approve":
            case "decline":
                return canEngineerDecide;
            case "retry":
                return canRetry;
            case "close":
                return canCloseRequest;
            case "rollback":
                return canRollback;
            default:
                return canIsoAct;
        }
    };

    // A decision prepared in chat ("decline #7 because ...") opens this panel on the submit step with the decision and reason filled in.
    // It only prepares: the person still ticks the confirmation and presses the button. Declared after the reset above so it wins on a fresh item.
    useEffect(() => {
        if (!pendingDraft || pendingDraft.id !== item.id) {
            return;
        }

        clearPendingDraft();
        if (!canEngineerDecide) {
            return; // it can no longer be decided: just show the item as it is
        }

        const action =
            pendingDraft.decision === "approve" ? "approve" : "decline";
        setPendingAction(action);
        setReason(pendingDraft.reason.trim() || defaultTextFor(action, item));
        setConfirmed(false);
        setActionError(null);
        setStage("submit");
        // defaultTextFor only reads the item, which is already in the dependency list through item.id.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingDraft, item.id, canEngineerDecide, clearPendingDraft]);

    const openSubmit = (action: ItemAction) => {
        setPendingAction(action);
        setReason(defaultTextFor(action, item));
        setConfirmed(false);
        setActionError(null);
        setStage("submit");
    };

    const runAction = async (action: ItemAction) => {
        const spec = ACTION_COPY[action];
        const needed = minLengthFor(action, item);

        // A retry has nothing to fill in: the backend re-reads the checks itself.
        if (action !== "retry") {
            if (!confirmed) {
                setActionError(`Tick the box to confirm: ${spec.confirm}`);
                return;
            }

            if (reason.trim().length < needed) {
                setActionError(
                    `${spec.field} must be at least ${needed} characters.`
                );
                return;
            }
        }

        if (isSubmitting) {
            return;
        }

        setPendingAction(action);
        setIsSubmitting(true);
        setActionError(null);
        setActionFailure(null);

        const usesProcessingStepper =
            action === "retry" || action === "rollback";

        if (usesProcessingStepper) {
            setProcessingAction(action);
            setProcessingOutcome("pending");
            setPendingActionResult(null);
            setPendingActionFailure(null);
            setProcessingRunId((current) => current + 1);
        }
        try {
            const updated = await onAction(action, reason.trim());
            if (usesProcessingStepper) {
                // Keep the API result temporarily.
                // The final success screen appears after the stepper finishes.
                setPendingActionResult(updated);
                setProcessingOutcome("success");
            } else {
                setActionResult(updated);
                setStage("success");
                setIsSubmitting(false);
            }
        } catch (error) {
            const message =
                error instanceof Error ? error.message : String(error);
            const failure: ActionFailure = {
                message,
                stale: error instanceof ApiError && error.isStale,
                reasons: error instanceof ApiError ? (error.reasons ?? []) : [],
            };

            if (usesProcessingStepper) {
                // Step 3 will display FAILED.
                setActionError(message);
                setPendingActionFailure(failure);
                setProcessingOutcome("failed");
            } else {
                setActionError(message);
                setActionFailure(failure);
                setStage("failure");
                setIsSubmitting(false);
            }
            // } finally {
            //     setIsSubmitting(false);
        }
    };

    const handleProcessingEnd = () => {
        setIsSubmitting(false);

        if (processingOutcome === "success" && pendingActionResult) {
            setActionResult(pendingActionResult);
            setActionFailure(null);
            setStage("success");
        } else if (processingOutcome === "failed" && pendingActionFailure) {
            setActionFailure(pendingActionFailure);
            setActionError(pendingActionFailure.message);
            setStage("failure");
        }

        setProcessingAction(null);
        setProcessingOutcome("pending");
        setPendingActionResult(null);
        setPendingActionFailure(null);
    };

    const createdLabel = item.createdAt
        ? new Date(item.createdAt).toLocaleString()
        : "unknown time";

    const sourceText = item.evidence.source
        ? item.evidence.sourcesAgree === true &&
          !item.evidence.source.toLowerCase().includes("agree")
            ? `${item.evidence.source}, agree`
            : item.evidence.sourcesAgree === false &&
                !item.evidence.source.toLowerCase().includes("disagree")
              ? `${item.evidence.source}, disagree`
              : item.evidence.source
        : null;

    const itemRef = item.evidence.item || item.id;
    const verifiedText = formatRelativeTime(item.evidence.verifiedAt);

    // Only what the backend sent: a bar with an invented value would be worse than no bar.
    const factors = item.whyRank?.priority?.factors;
    const percent = (value: number | undefined) =>
        value === undefined ? null : Math.round(value * 100);
    const breakdownItems = [
        {
            label: "Business impact",
            value: percent(factors?.severity),
            color: "coral",
        },
        {
            label: "Confidence",
            value:
                item.confidence?.score ??
                item.whyRank?.confidence?.score ??
                null,
            color: "green",
        },
        {
            label: "Reversibility",
            value: percent(factors?.actionability),
            color: "green",
        },
        {
            label: "Blast radius",
            value: percent(factors?.exposure),
            color: "coral",
        },
    ].filter(
        (entry): entry is { label: string; value: number; color: string } =>
            entry.value !== null
    );

    const workNoteLines = [
        `[AIDWops] DECISION: ${copy.note}`,
        `Proposal: ${item.id} · ${item.title}`,
        `${isIso ? "Approver" : "Reviewer"}: ${reviewerName}`,
        `AI priority: ${item.priority.score}/100 · risk ${item.risk}`,
        `${copy.word || "Comment"}: ${reason.trim() || "(none)"}`,
    ];

    // The activity list under the ticket. `timeline` is the readable one (who did what, with the note they left);
    // an older backend only has the raw audit trail.
    const activity: Array<{
        at: string | null;
        by: string;
        role: string;
        title: string;
        note: string;
    }> =
        item.timeline ??
        (item.audit ?? []).map((entry) => ({
            at: entry.at,
            by: entry.actor,
            role: "",
            title: entry.event.replace(/_/g, " "),
            note: entry.detail,
        }));

    // What the backend answered after the call, not what this screen assumes happened.
    const savedItem = actionResult ?? item;
    const savedStatusLabel =
        savedItem.statusLabel ?? savedItem.status.replace(/_/g, " ");
    const savedRecord =
        pendingAction === "approve" || pendingAction === "decline"
            ? savedItem.decision
            : pendingAction === "iso_approve" || pendingAction === "iso_reject"
              ? savedItem.iso
              : null;
    const savedComment = savedRecord?.comment ?? reason.trim();
    const savedLines = [
        `Status: ${savedStatusLabel}`,
        `Item: ${savedItem.id} · ${savedItem.title}`,
        ...(pendingAction === "retry"
            ? []
            : [
                  `By: ${savedRecord?.by ?? reviewerName}`,
                  ...(savedComment ? [`${copy.word}: ${savedComment}`] : []),
              ]),
    ];
    const savedSummary =
        savedItem.lifecycle?.summary[isIso ? "iso" : "engineer"];

    const firstTicket = savedItem.tickets?.[0];
    const ticketSystemLabel = (system: string) =>
        system === "jira" ? "Jira" : "ServiceNow";

    const failureTitle = actionFailure?.stale
        ? "This item changed since you opened it."
        : actionFailure?.reasons.length
          ? "This cannot be applied as asked."
          : copy.failSubtitle;

    const displayTitle =
        stage === "success"
            ? `${copy.done} · ${item.title}`
            : stage === "failure"
              ? `${copy.noun} failed · ${item.title}`
              : stage === "submit"
                ? `${copy.verb} · ${item.title}`
                : item.title;

    // Status, in the words of the side that is looking.
    const statusLabel = item.statusLabel ?? item.status.replace(/_/g, " ");
    const statusSummary = item.lifecycle?.summary[isIso ? "iso" : "engineer"];
    const steps = item.lifecycle?.steps ?? [];

    // An engineer is about to approve something the backend will refuse: say so before they spend a comment on it.
    const failingChecks = item.checks.filter(
        (check) => check.status === "fail"
    );
    const productionAllows = item.production?.verdict === "allowed";
    const willBeRefused =
        canEngineerDecide &&
        item.actionType === "apply_dev" &&
        failingChecks.length > 0;

    const showRequest =
        item.source !== "detector" && (item.requester || item.justification);

    // The engineer already decided: the investigative sections that led to the decision
    // (found, evidence, checks, plan, target/impact, breakdown, rank) no longer add anything
    // for them, so hide them once `item.decision` is on record. The ISO still sees them.
    const engineerDecided = !isIso && !!item.decision;

    return (
        <article className="expert-queue-item-detail-card">
            <div className="expert-queue-item-detail-card-header-bar">
                <button
                    type="button"
                    className="expert-queue-item-detail-card-back"
                    onClick={() => {
                        if (stage === "submit") {
                            setStage("preview");
                            setActionError(null);
                        } else if (stage === "failure") {
                            setStage(
                                pendingAction === "retry" ? "preview" : "submit"
                            );
                            setActionError(null);
                        } else {
                            dismiss();
                        }
                    }}
                >
                    <Icon name="chevron_small_left" />
                    Back
                </button>

                <div className="expert-queue-item-detail-card-head">
                    <span
                        className={`expert-queue-item-detail-card-icon ${
                            stage === "success"
                                ? "expert-queue-item-detail-card-icon--success"
                                : "expert-queue-item-detail-card-icon--red"
                        }`}
                    >
                        <EonUiIcon
                            name={
                                stage === "success"
                                    ? "radio_checkbox_tick"
                                    : stage === "failure"
                                      ? "alert"
                                      : "edit"
                            }
                            size="small"
                            style={{
                                color:
                                    stage === "success"
                                        ? "var(--success)"
                                        : "var(--eon-red)",
                            }}
                        />
                    </span>
                    <div className="expert-queue-item-detail-card-head-text">
                        <h3 className="expert-queue-item-detail-card-title">
                            {displayTitle}
                        </h3>
                        {stage === "preview" && (
                            <p className="expert-queue-item-detail-card-subtitle expert-queue-item-detail-card-subtitle--red">
                                {`Rank #${item.rank ?? "–"} · ${item.id} · created ${createdLabel}`}
                            </p>
                        )}
                        {stage === "submit" && (
                            <p className="expert-queue-item-detail-card-subtitle">
                                {copy.submitSubtitle}
                            </p>
                        )}
                        {stage === "success" && (
                            <p className="expert-queue-item-detail-card-subtitle">
                                {`${reviewerName} · just now`}
                            </p>
                        )}
                        {stage === "failure" && (
                            <p className="expert-queue-item-detail-card-subtitle">
                                {copy.failSubtitle}
                            </p>
                        )}
                    </div>
                    {stage === "preview" && (
                        <div className="expert-queue-item-detail-card-priority">
                            <span className="expert-queue-item-detail-card-priority-value">
                                {item.priority.score}
                            </span>
                            <span className="expert-queue-item-detail-card-priority-label">
                                Priority
                            </span>
                        </div>
                    )}
                    {stage === "success" && (
                        <span className="expert-queue-view-tag expert-queue-view-tag--low">
                            {savedStatusLabel}
                        </span>
                    )}
                    {stage === "failure" && (
                        <span className="expert-queue-view-tag expert-queue-view-tag--high">
                            Not saved
                        </span>
                    )}
                </div>
            </div>

            <div className="expert-queue-item-detail-card-body">
                {!processingAction && stage === "preview" && (
                    <>
                        <div className="expert-queue-view-tags">
                            <span className="expert-queue-view-tag expert-queue-view-tag--context">
                                {item.product}
                            </span>
                            <span
                                className={`expert-queue-view-tag expert-queue-view-tag--${item.risk}`}
                            >
                                {item.risk} risk
                            </span>
                            {item.tag && (
                                <span className="expert-queue-view-tag expert-queue-view-tag--kind">
                                    {item.tag}
                                </span>
                            )}
                        </div>

                        <div className="expert-queue-item-detail-card-section">
                            <div className="expert-queue-item-detail-card-section-label">
                                Status
                            </div>
                            <p className="expert-queue-item-detail-card-section-text">
                                <strong>{statusLabel}</strong>
                                {statusSummary ? ` · ${statusSummary}` : ""}
                            </p>
                            {item.status === "apply_failed" &&
                                item.applyError &&
                                !statusSummary?.includes(item.applyError) && (
                                    <div className="ac-alert" role="alert">
                                        <p className="ac-alert-title">
                                            The write to Zscaler failed.
                                        </p>
                                        <p className="ac-alert-detail">
                                            {item.applyError}
                                        </p>
                                    </div>
                                )}
                            {steps.length > 0 && (
                                <LifecycleStepper steps={steps} />
                            )}
                        </div>

                        {willBeRefused && (
                            <div className="ac-alert" role="alert">
                                <p className="ac-alert-title">
                                    {productionAllows
                                        ? "Production already allows this URL."
                                        : "A pre-flight check fails."}
                                </p>
                                <ul
                                    className="ac-alert-detail"
                                    style={{
                                        margin: "4px 0 0",
                                        paddingLeft: 18,
                                    }}
                                >
                                    {failingChecks.map((check) => (
                                        <li key={check.id || check.label}>
                                            {check.detail}
                                        </li>
                                    ))}
                                </ul>
                                <p className="ac-alert-detail">
                                    Approving re-checks and is refused while
                                    this holds, so it can only be declined.
                                </p>
                            </div>
                        )}

                        {!engineerDecided && (
                            <div className="expert-queue-item-detail-card-section">
                                <div className="expert-queue-item-detail-card-section-label">
                                    What I found
                                </div>
                                <p className="expert-queue-item-detail-card-section-text">
                                    {item.found}
                                </p>
                                <p className="expert-queue-item-detail-card-recommend">
                                    AI recommends:{" "}
                                    <strong>{item.recommendation.label}</strong>
                                </p>
                                {item.previouslyDeclinedHours !== null &&
                                    item.previouslyDeclinedHours !==
                                        undefined && (
                                        <p className="expert-queue-item-detail-card-section-text">
                                            The same thing was declined{" "}
                                            {hoursAgo(
                                                item.previouslyDeclinedHours
                                            )}
                                            .
                                        </p>
                                    )}
                            </div>
                        )}

                        {showRequest && (
                            <div className="expert-queue-item-detail-card-section">
                                <div className="expert-queue-item-detail-card-section-label">
                                    Request
                                </div>
                                <div className="expert-queue-item-detail-card-evidence-box">
                                    {item.requester && (
                                        <div className="expert-queue-item-detail-card-kv-row">
                                            <span className="expert-queue-item-detail-card-kv-label">
                                                Requested by
                                            </span>
                                            <span className="expert-queue-item-detail-card-kv-value">
                                                {item.requester}
                                            </span>
                                        </div>
                                    )}
                                    {item.justification && (
                                        <div className="expert-queue-item-detail-card-kv-row">
                                            <span className="expert-queue-item-detail-card-kv-label">
                                                Reason
                                            </span>
                                            <span className="expert-queue-item-detail-card-kv-value">
                                                {item.justification}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* {item.production && (
                        <details className="expert-queue-item-detail-card-fold">
                            <summary>Production today</summary>
                            <div className="expert-queue-item-detail-card-fold-body">
                                {item.production.verdict !== "unknown" && (
                                    <div className="expert-queue-view-tags">
                                        <span
                                            className={`expert-queue-view-tag expert-queue-view-tag--${PRODUCTION_VERDICT[item.production.verdict].tone}`}
                                        >
                                            {PRODUCTION_VERDICT[item.production.verdict].text}
                                        </span>
                                    </div>
                                )}
                                <p className="expert-queue-item-detail-card-section-text">
                                    {item.production.summary}
                                </p>
                            </div>
                        </details>
                    )} */}

                        {!engineerDecided && (
                            <div className="expert-queue-item-detail-card-section">
                                <div className="expert-queue-item-detail-card-section-label">
                                    Evidence
                                </div>
                                <div className="expert-queue-item-detail-card-evidence-box">
                                    {itemRef && (
                                        <div className="expert-queue-item-detail-card-kv-row">
                                            <span className="expert-queue-item-detail-card-kv-label">
                                                Item
                                            </span>
                                            <span className="expert-queue-item-detail-card-kv-value">
                                                {itemRef}
                                            </span>
                                        </div>
                                    )}
                                    {sourceText && (
                                        <div className="expert-queue-item-detail-card-kv-row">
                                            <span className="expert-queue-item-detail-card-kv-label">
                                                Source
                                            </span>
                                            <span className="expert-queue-item-detail-card-kv-value">
                                                {sourceText}
                                            </span>
                                        </div>
                                    )}
                                    {verifiedText && (
                                        <div className="expert-queue-item-detail-card-kv-row">
                                            <span className="expert-queue-item-detail-card-kv-label">
                                                Verified
                                            </span>
                                            <span
                                                className="expert-queue-item-detail-card-kv-value"
                                                title={
                                                    item.evidence.verifiedAt
                                                        ? new Date(
                                                              item.evidence
                                                                  .verifiedAt
                                                          ).toLocaleString()
                                                        : undefined
                                                }
                                            >
                                                {verifiedText}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                {item.evidence.points.length > 0 && (
                                    <ul className="expert-queue-item-detail-card-evidence-list">
                                        {item.evidence.points.map(
                                            (point, index) => (
                                                <li key={`${point}-${index}`}>
                                                    {point}
                                                </li>
                                            )
                                        )}
                                    </ul>
                                )}
                                {item.evidence.sample.length > 0 && (
                                    <>
                                        <div className="expert-queue-item-detail-card-section-label">
                                            Examples
                                        </div>
                                        <ul className="expert-queue-item-detail-card-evidence-list">
                                            {item.evidence.sample.map(
                                                (example, index) => (
                                                    <li
                                                        key={`${example}-${index}`}
                                                    >
                                                        {example}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </>
                                )}
                            </div>
                        )}

                        {item.kind === "intune_battery_attention" && (
                            <BatteryDevicesSection key={item.id} item={item} />
                        )}
                        {item.kind === "intune_policy_hygiene" && (
                            <PolicyHygieneSection key={item.id} item={item} />
                        )}

                        {item.checks.length > 0 && !engineerDecided && (
                            <div className="expert-queue-item-detail-card-section">
                                <div className="expert-queue-item-detail-card-section-label">
                                    Pre-flight checks
                                </div>
                                <ul className="expert-queue-item-detail-card-checks">
                                    {item.checks.map((check) => {
                                        const checkKey =
                                            check.id || check.label;
                                        const icon = (
                                            <span
                                                className={`expert-queue-item-detail-card-check-icon expert-queue-item-detail-card-check-icon--${check.status}`}
                                            >
                                                <CheckIcon
                                                    status={check.status}
                                                />
                                            </span>
                                        );
                                        return (
                                            <li
                                                key={checkKey}
                                                className={`expert-queue-item-detail-card-check expert-queue-item-detail-card-check--${check.status}`}
                                            >
                                                {check.detail ? (
                                                    <details className="expert-queue-item-detail-card-check-fold">
                                                        <summary className="expert-queue-item-detail-card-check-summary">
                                                            <span className="expert-queue-item-detail-card-check-summary-content">
                                                                {icon}
                                                                <span className="expert-queue-item-detail-card-check-title">
                                                                    {
                                                                        check.label
                                                                    }
                                                                </span>
                                                            </span>
                                                        </summary>
                                                        <div className="expert-queue-item-detail-card-check-detail">
                                                            {check.detail}
                                                        </div>
                                                    </details>
                                                ) : (
                                                    <div className="expert-queue-item-detail-card-check-summary">
                                                        <span className="expert-queue-item-detail-card-check-summary-content">
                                                            {icon}
                                                            <span className="expert-queue-item-detail-card-check-title">
                                                                {check.label}
                                                            </span>
                                                        </span>
                                                    </div>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}

                        {item.steps.length > 0 && !engineerDecided && (
                            <div className="expert-queue-item-detail-card-section">
                                <div className="expert-queue-item-detail-card-section-label">
                                    What I will do once approved
                                </div>
                                <div className="expert-queue-item-detail-card-steps">
                                    {item.steps.map((step, idx) => {
                                        const badgeText = (
                                            step.op ||
                                            (idx === 0 ? "TASK" : "NOTE")
                                        ).toUpperCase();
                                        return (
                                            <div
                                                key={step.n || idx}
                                                className="expert-queue-item-detail-card-step-row"
                                            >
                                                <span className="expert-queue-item-detail-card-step-badge">
                                                    {badgeText}
                                                </span>
                                                <div className="expert-queue-item-detail-card-step-content">
                                                    <div className="expert-queue-item-detail-card-step-text">
                                                        {step.text}
                                                    </div>
                                                    {step.detail && (
                                                        <div className="expert-queue-item-detail-card-step-detail">
                                                            {step.detail}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {item.target && !engineerDecided && (
                            <div className="expert-queue-item-detail-card-section">
                                <div className="expert-queue-item-detail-card-section-label">
                                    Target and impact
                                </div>
                                <div className="expert-queue-item-detail-card-evidence-box">
                                    <div className="expert-queue-item-detail-card-kv-row">
                                        <span className="expert-queue-item-detail-card-kv-label">
                                            Environment
                                        </span>
                                        <span className="expert-queue-item-detail-card-kv-value">
                                            {item.target.environment === "DEV"
                                                ? `DEV · ${item.target.tenant}`
                                                : "No system is changed"}
                                        </span>
                                    </div>
                                    <div className="expert-queue-item-detail-card-kv-row">
                                        <span className="expert-queue-item-detail-card-kv-label">
                                            Action
                                        </span>
                                        <span className="expert-queue-item-detail-card-kv-value">
                                            {item.target.action}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {breakdownItems.length > 0 && !engineerDecided && (
                            <div className="expert-queue-item-detail-card-section">
                                <div className="expert-queue-item-detail-card-section-label">
                                    Priority breakdown
                                </div>
                                <div className="expert-queue-item-detail-card-breakdown">
                                    {breakdownItems.map((b) => (
                                        <div
                                            key={b.label}
                                            className="expert-queue-item-detail-card-breakdown-row"
                                        >
                                            <span className="expert-queue-item-detail-card-breakdown-label">
                                                {b.label}
                                            </span>
                                            <div className="expert-queue-item-detail-card-breakdown-bar">
                                                <div
                                                    className={`expert-queue-item-detail-card-breakdown-fill expert-queue-item-detail-card-breakdown-fill--${b.color}`}
                                                    style={{
                                                        width: `${Math.min(100, Math.max(0, b.value))}%`,
                                                    }}
                                                />
                                            </div>
                                            <span className="expert-queue-item-detail-card-breakdown-value">
                                                {b.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {item.whyRank && !engineerDecided && (
                            <details className="expert-queue-item-detail-card-fold">
                                <summary>Why this rank?</summary>
                                <div className="expert-queue-item-detail-card-fold-body">
                                    <p>
                                        Priority {item.whyRank.priority.score}
                                        /100
                                        {item.whyRank.priority.reasons.length >
                                        0
                                            ? `: ${item.whyRank.priority.reasons.join(" and ")}`
                                            : ""}
                                        .
                                    </p>
                                    <p>
                                        Confidence{" "}
                                        {item.whyRank.confidence.score}
                                        /100 ({item.whyRank.confidence.band});
                                        weakest factor:{" "}
                                        {item.whyRank.confidence.weakest}
                                        {item.whyRank.confidence.caps.length > 0
                                            ? `. Held back by: ${item.whyRank.confidence.caps.join(", ")}`
                                            : ""}
                                        .
                                    </p>
                                    <p>
                                        The recommendation is derived by rules
                                        from the kind, the checks and the
                                        confidence. A person decides.
                                    </p>
                                </div>
                            </details>
                        )}

                        {(item.decision || item.iso) && (
                            <div className="expert-queue-item-detail-card-section">
                                <div className="expert-queue-item-detail-card-section-label">
                                    Decisions
                                </div>
                                <div className="expert-queue-item-detail-card-evidence-box">
                                    {item.decision && (
                                        <DecisionLine
                                            label="Engineer"
                                            record={item.decision}
                                            outcome={
                                                item.decision.decision ===
                                                "decline"
                                                    ? "Declined"
                                                    : "Approved"
                                            }
                                        />
                                    )}
                                    {item.iso && (
                                        <DecisionLine
                                            label="ISO"
                                            record={item.iso}
                                            outcome={
                                                item.iso.approved === false
                                                    ? "Rejected"
                                                    : "Approved"
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        )}

                        <LinkedTicketsView tickets={item.tickets} />

                        {activity.length > 0 && (
                            <div className="expert-queue-item-detail-card-section">
                                <div className="expert-queue-item-detail-card-section-label">
                                    Activity
                                </div>
                                <ul className="expert-queue-item-detail-card-timeline">
                                    {activity.map((entry, index) => (
                                        <li key={`${entry.at ?? ""}-${index}`}>
                                            <div
                                                className="expert-queue-item-detail-card-timeline-time"
                                                title={
                                                    entry.at
                                                        ? new Date(
                                                              entry.at
                                                          ).toLocaleString()
                                                        : undefined
                                                }
                                            >
                                                {formatRelativeTime(entry.at)}
                                                {entry.role
                                                    ? ` · ${entry.role}`
                                                    : ""}
                                            </div>
                                            <div className="expert-queue-item-detail-card-timeline-title">
                                                <strong>{entry.by}</strong> ·{" "}
                                                {entry.title}
                                            </div>
                                            {entry.note && (
                                                <div className="expert-queue-item-detail-card-timeline-note">
                                                    {entry.note}
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {onComment && item.tickets.length > 0 && (
                            <CommentBox onComment={onComment} />
                        )}
                    </>
                )}

                {stage === "submit" && (
                    <>
                        <div className="expert-queue-item-detail-card-section">
                            <div className="expert-queue-item-detail-card-section-label">
                                {`${copy.field}${requiredLength > 0 ? " (required)" : ""}`.toUpperCase()}
                            </div>
                            <textarea
                                className="expert-queue-item-detail-card-comment-input"
                                id="queue-decision-reason"
                                value={reason}
                                onChange={(event) => {
                                    setReason(event.target.value);
                                    if (actionError) setActionError(null);
                                }}
                                rows={3}
                                required={requiredLength > 0}
                                placeholder={copy.placeholder}
                                aria-label={`${copy.field}${requiredLength > 0 ? " (required)" : ""}`}
                            />
                            <div className="expert-queue-item-detail-card-comment-hints">
                                <span>
                                    {reason.trim().length >= requiredLength
                                        ? requiredLength > 0
                                            ? "Looks good."
                                            : ""
                                        : `${reason.trim().length}/${requiredLength} characters min.`}
                                </span>
                                <span>{copy.stateHint}</span>
                            </div>
                        </div>
                        <Worknote
                            workNoteLines={workNoteLines}
                            title="DECISION SUMMARY"
                        />
                        <label className="queue-decision__confirmation">
                            <input
                                type="checkbox"
                                checked={confirmed}
                                onChange={(event) =>
                                    setConfirmed(event.target.checked)
                                }
                            />
                            {copy.confirm}
                        </label>
                        {actionError && (
                            <p className="queue-decision__error" role="alert">
                                {actionError}
                            </p>
                        )}
                    </>
                )}

                {stage === "success" && (
                    <>
                        {steps.length > 0 && <LifecycleStepper steps={steps} />}
                        <Worknote
                            workNoteLines={savedLines}
                            title="DECISION RECORDED"
                        />
                        <div className="expert-queue-item-detail-card-section expert-queue-item-detail-card-section--success">
                            {savedSummary && (
                                <p className="expert-queue-item-detail-card-section-text">
                                    <strong>{savedSummary}</strong>
                                </p>
                            )}
                            {firstTicket && (
                                <LinkedTicketsView
                                    tickets={savedItem.tickets}
                                />
                            )}
                        </div>
                    </>
                )}

                {stage === "failure" && (
                    <>
                        <div className="ac-alert" role="alert">
                            <p className="ac-alert-title">{failureTitle}</p>
                            {actionFailure?.reasons.length ? (
                                <ul
                                    className="ac-alert-detail"
                                    style={{
                                        margin: "4px 0 0",
                                        paddingLeft: 18,
                                    }}
                                >
                                    {actionFailure.reasons.map((reasonText) => (
                                        <li key={reasonText}>{reasonText}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="ac-alert-detail">
                                    {actionFailure?.stale
                                        ? "Reload the item to see its current state, then try again."
                                        : actionError ||
                                          "Try again, or reload the item."}
                                </p>
                            )}
                        </div>

                        {pendingAction !== "retry" && (
                            <Worknote
                                workNoteLines={workNoteLines}
                                title="DECISION SUMMARY"
                            />
                        )}
                    </>
                )}

                {processingAction && isSubmitting && (
                    <ProcessingStepper
                        key={`${processingAction}-${processingRunId}`}
                        status={processingOutcome}
                        steps={processingSteps}
                        stepDurationsMs={{
                            first: 500,
                            second: 2000,
                            fourth: 1000,
                        }}
                        endProcess={handleProcessingEnd}
                    />
                )}
            </div>

            <div className="expert-queue-item-detail-card-footer ac-footer">
                {stage === "preview" && (
                    <>
                        {canEngineerDecide && (
                            <>
                                <EonUiButton
                                    text={
                                        recommendedAction === "decline"
                                            ? "Cancel"
                                            : "Decline"
                                    }
                                    rank="secondary"
                                    size="small"
                                    disabled={isSubmitting}
                                    onClick={
                                        recommendedAction === "decline"
                                            ? dismiss
                                            : () => openSubmit("decline")
                                    }
                                />
                                <EonUiButton
                                    text={
                                        item.recommendation.label || "Approve"
                                    }
                                    rank="primary"
                                    size="small"
                                    disabled={isSubmitting}
                                    onClick={() =>
                                        openSubmit(recommendedAction)
                                    }
                                />
                            </>
                        )}
                        {canIsoAct && (
                            <>
                                <EonUiButton
                                    text="Reject"
                                    rank="secondary"
                                    size="small"
                                    disabled={isSubmitting}
                                    onClick={() => openSubmit("iso_reject")}
                                />
                                <EonUiButton
                                    text="Approve"
                                    rank="primary"
                                    size="small"
                                    disabled={isSubmitting}
                                    onClick={() => openSubmit("iso_approve")}
                                />
                            </>
                        )}
                        {canRetry && (
                            <EonUiButton
                                text="Retry apply"
                                rank="primary"
                                size="small"
                                disabled={isSubmitting}
                                preloader={isSubmitting}
                                onClick={() => void runAction("retry")}
                            />
                        )}
                        {canRollback && (
                            <EonUiButton
                                text="Roll back"
                                rank="secondary"
                                size="small"
                                disabled={isSubmitting}
                                onClick={() => openSubmit("rollback")}
                            />
                        )}
                        {canCloseRequest && (
                            <EonUiButton
                                text="Apply & Close"
                                rank="primary"
                                size="small"
                                disabled={isSubmitting}
                                onClick={() => openSubmit("close")}
                            />
                        )}
                    </>
                )}
                {stage === "submit" && (
                    <>
                        <EonUiButton
                            text="Cancel"
                            rank="secondary"
                            size="small"
                            disabled={isSubmitting}
                            onClick={dismiss}
                        />
                        <EonUiButton
                            text={
                                pendingAction === recommendedAction
                                    ? item.recommendation.label || copy.verb
                                    : copy.verb
                            }
                            rank="primary"
                            size="small"
                            disabled={!canDo(pendingAction) || isSubmitting}
                            preloader={isSubmitting}
                            onClick={() => void runAction(pendingAction)}
                        />
                    </>
                )}
                {stage === "success" && (
                    <EonUiButton
                        text="Close"
                        rank="primary"
                        size="small"
                        onClick={dismiss}
                    />
                )}
                {stage === "failure" && (
                    <>
                        <EonUiButton
                            text="Back"
                            rank="secondary"
                            size="small"
                            disabled={isSubmitting}
                            onClick={() => {
                                setStage(
                                    pendingAction === "retry"
                                        ? "preview"
                                        : "submit"
                                );
                                setActionError(null);
                            }}
                        />
                        {actionFailure?.stale ? (
                            <EonUiButton
                                text="Reload item"
                                rank="primary"
                                size="small"
                                disabled={isSubmitting}
                                onClick={() => {
                                    if (onReload) {
                                        void onReload();
                                    } else {
                                        dismiss();
                                    }
                                }}
                            />
                        ) : (
                            <EonUiButton
                                text="Retry"
                                rank="primary"
                                size="small"
                                disabled={isSubmitting}
                                preloader={isSubmitting}
                                onClick={() => void runAction(pendingAction)}
                            />
                        )}
                    </>
                )}
            </div>
        </article>
    );
}

export const ExpertQueueItemDetailCard = QueueItemDetailCard;
export default QueueItemDetailCard;
